'use client';

import React, { Component, ReactNode } from 'react';

import { Button } from '@components';
import Modal, { ActionBlock, IconBlock, MessageBlock, Title } from '@components/Modal';

import styles from './ErrorBoundary.module.scss';

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  showStackTrace?: boolean;
  formatted: boolean;
};

type ErrorBoundaryProps = {
  children: ReactNode;
  showErrorModal?: boolean; // based on env but can also be passed in as a prop
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    formatted: true,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      formatted: true,
    };
  }

  componentDidCatch(error: Error) {
    this.setState({ error });
  }

  toggleStackTraceFormat = () => {
    this.setState((prevState) => ({ formatted: !prevState.formatted }));
  };

  render() {
    const { hasError, error, formatted } = this.state;
    let { showErrorModal } = this.props;

    showErrorModal =
      showErrorModal || (process.env.NODE_ENV !== 'development' && process.env.NODE_ENV !== 'test');

    if (hasError) {
      if (showErrorModal) {
        return (
          <Modal testId="error-modal" minWidth="45rem" rounded>
            <IconBlock icon="warning" />
            <Title title="Error" fontSize="large" />
            <MessageBlock messages={[error?.message ?? 'An error has occurred']} />
            <ActionBlock
              actions={[
                {
                  text: 'Refresh Page',
                  onClick: () => window.location.reload(),
                  appearance: 'primary',
                },
                {
                  text: 'Restart App',
                  onClick: () => window.location.assign('/'),
                  appearance: 'primary',
                },
              ]}
            />
          </Modal>
        );
      } else {
        const stackTrace = error?.stack ?? '';

        const formatTraceLine = (line: string) => {
          const regex =
            /at\s+([^\s]+)\s+\((webpack-internal:\/\/\/\(app-pages-browser\)\/.\/src\/([^:]+):(\d+):(\d+))\)/;
          const match = regex.exec(line);

          if (match) {
            const identifier = match[1];
            const internalPath = match[3];
            const lineNumber = match[4];
            const columnNumber = match[5];

            return (
              <div>
                at <strong>{identifier}</strong> ({internalPath}:{lineNumber}:{columnNumber}){' '}
              </div>
            );
          }
          return <div>{line}</div>;
        };

        const stackTraceDisplay = formatted ? (
          stackTrace.split('\n').map((line) => (
            <div key={line} className={styles.stackTraceLine}>
              {formatTraceLine(line)}
            </div>
          ))
        ) : (
          <pre>{stackTrace}</pre>
        );

        return (
          <div className={styles.errorPage}>
            <div className={styles.errorPageTitle}>
              <h1>Error Occurred</h1>
              <p>{this.state.error?.toString() ?? 'An error has occurred'}</p>
            </div>
            <div className={styles.stackTraceContainer}>
              <Button
                appearance="text"
                onClick={this.toggleStackTraceFormat}
                className={styles.textButton}
                text="Toggle Formatting"
              />
              <div className={styles.stackTrace}>{stackTraceDisplay}</div>
            </div>
            <div className={styles.actions}>
              <Button
                text="Refresh Page"
                appearance="primary"
                slim
                onClick={() => window.location.reload()}
              />
              <Button
                text="Restart App"
                appearance="primary"
                slim
                onClick={() => window.location.assign('/')}
              />
            </div>
          </div>
        );
      }
    }
    return this.props.children;
  }
}
